import React, { useState, useEffect } from 'react';
import resourcesService from '../services/resourcesService';
import promptService from '../services/promptService';

/**
 * Modal component for managing resource categories.
 * 
 * Allows admins to create, edit, and delete resource categories,
 * and associate them with prompts from the prompt manager.
 */
const CategoryManagementModal = ({ isOpen, onClose, onCategoryChange, categories }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('list'); // 'list', 'create', 'edit'

  // Fetch prompts when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPrompts();
    }
  }, [isOpen]);

  const fetchPrompts = async () => {
    setLoadingPrompts(true);
    try {
      const response = await promptService.getPrompts();
      setPrompts(response.prompts || []);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setError('Failed to load prompts');
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleCreateNew = () => {
    setMode('create');
    setSelectedCategory(null);
    setCategoryName('');
    setCategoryDescription('');
    setSelectedPromptId('');
    setError('');
  };

  const handleEditCategory = (category) => {
    setMode('edit');
    setSelectedCategory(category);
    setCategoryName(category.category_name);
    setCategoryDescription(category.description || '');
    setSelectedPromptId(category.prompt_id || '');
    setError('');
  };

  const handleCancel = () => {
    setMode('list');
    setSelectedCategory(null);
    setCategoryName('');
    setCategoryDescription('');
    setSelectedPromptId('');
    setError('');
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    
    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const categoryData = {
        category_name: categoryName.trim(),
        description: categoryDescription.trim() || null,
        prompt_id: selectedPromptId || null
      };

      if (mode === 'create') {
        await resourcesService.createCategory(categoryData);
      } else if (mode === 'edit' && selectedCategory) {
        await resourcesService.updateCategory(selectedCategory.category_id, categoryData);
      }

      // Notify parent to refresh categories
      if (onCategoryChange) {
        await onCategoryChange();
      }

      handleCancel();
    } catch (error) {
      console.error('Error saving category:', error);
      setError(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await resourcesService.deleteCategory(categoryId);
      
      // Notify parent to refresh categories
      if (onCategoryChange) {
        await onCategoryChange();
      }

      if (selectedCategory && selectedCategory.category_id === categoryId) {
        handleCancel();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setError(error.response?.data?.detail || 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-gear me-2"></i>
              Manage Resource Categories
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={loading}></button>
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}

            {mode === 'list' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Categories</h6>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleCreateNew}
                    disabled={loading}
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Create New Category
                  </button>
                </div>

                <div className="list-group">
                  {categories.length === 0 && (
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-folder-x display-4 mb-3"></i>
                      <p>No categories found. Create one to get started!</p>
                    </div>
                  )}
                  
                  {categories.map((category) => (
                    <div
                      key={category.category_id}
                      className="list-group-item"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{category.category_name}</h6>
                          {category.description && (
                            <p className="mb-1 text-muted small">{category.description}</p>
                          )}
                          {category.prompt_id && (
                            <small className="text-info">
                              <i className="bi bi-cpu me-1"></i>
                              Prompt: {prompts.find(p => p.prompt_id === category.prompt_id)?.name || category.prompt_id}
                            </small>
                          )}
                        </div>
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditCategory(category)}
                            disabled={loading}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteCategory(category.category_id)}
                            disabled={loading}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(mode === 'create' || mode === 'edit') && (
              <form onSubmit={handleSaveCategory}>
                <div className="mb-3">
                  <label htmlFor="categoryName" className="form-label">
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="categoryName"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    placeholder="Enter category name"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="categoryDescription" className="form-label">
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    id="categoryDescription"
                    rows="3"
                    value={categoryDescription}
                    onChange={(e) => setCategoryDescription(e.target.value)}
                    placeholder="Enter category description (optional)"
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="promptSelect" className="form-label">
                    Associated Prompt
                  </label>
                  {loadingPrompts ? (
                    <div className="text-center py-2">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Loading prompts...
                    </div>
                  ) : (
                    <select
                      className="form-select"
                      id="promptSelect"
                      value={selectedPromptId}
                      onChange={(e) => setSelectedPromptId(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">No prompt (use default)</option>
                      {prompts.map((prompt) => (
                        <option key={prompt.prompt_id} value={prompt.prompt_id}>
                          {prompt.name} {prompt.description ? `- ${prompt.description}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  <div className="form-text">
                    Select a prompt from the prompt manager to customize AI responses for this category.
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        {mode === 'create' ? 'Create Category' : 'Update Category'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementModal;
